import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  GridLegacy as Grid,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Flag as FlagIcon,
  DirectionsRun as RunIcon,
} from '@mui/icons-material';
import { seasonService } from '../../services/seasonService';
import { SeasonSnapshot } from '../../types';
import { format } from 'date-fns';

interface SeasonHistorySectionProps {
  userId: string;
  teamId: string;
}

const SeasonHistorySection: React.FC<SeasonHistorySectionProps> = ({
  userId,
  teamId,
}) => {
  const [seasons, setSeasons] = useState<SeasonSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSeasonHistory();
  }, [userId, teamId]);

  const loadSeasonHistory = async () => {
    setLoading(true);
    setError('');
    
    try {
      const history = await seasonService.getUserSeasonHistory(userId, teamId);
      setSeasons(history);
    } catch (err: any) {
      console.error('Error loading season history:', err);
      setError(err.message || 'Failed to load season history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  /** PR time (decimal minutes) → M:SS; use rounded total seconds to avoid float drift */
  const formatTime = (minutes: number): string => {
    const totalSeconds = Math.max(0, Math.round(Number(minutes) * 60));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (seasons.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No season history yet. Season snapshots will appear here after your coach resets the season.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {seasons.map((season, index) => (
        <Card key={season.id} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {season.seasonName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ended: {formatDate(season.resetDate)}
                </Typography>
              </Box>
              <Chip
                label={`Level ${season.level}`}
                color="primary"
                size="small"
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Experience Points
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {season.experiencePoints.toFixed(1)} XP
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Wallet Points
                  </Typography>
                  <Typography variant="h6" color="secondary">
                    {season.walletPoints.toFixed(1)} WP
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Streak
                  </Typography>
                  <Typography variant="h6">
                    {season.currentStreak} days
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Longest Streak
                  </Typography>
                  <Typography variant="h6">
                    {season.longestStreak} days
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<RunIcon />}
                label={`${season.totalActivities} Activities`}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<TrophyIcon />}
                label={`${season.achievements.length} Achievements`}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<StarIcon />}
                label={`${season.badges.length} Badges`}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<FlagIcon />}
                label={`${season.personalRecords.length} PRs`}
                variant="outlined"
                size="small"
              />
            </Box>

            {/* Expandable sections for achievements, badges, and PRs */}
            {season.achievements.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Achievements ({season.achievements.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {season.achievements.map((achievement) => (
                      <Box key={achievement.id} sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {achievement.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {achievement.description}
                        </Typography>
                        {achievement.pointsAwarded > 0 && (
                          <Chip
                            label={`+${achievement.pointsAwarded} WP`}
                            size="small"
                            color="secondary"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {season.badges.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Badges ({season.badges.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {season.badges.map((badge) => (
                      <Chip
                        key={badge.id}
                        icon={<TrophyIcon />}
                        label={`Badge ${badge.badgeId}`}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {season.personalRecords.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Personal Records ({season.personalRecords.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {season.personalRecords.map((pr) => (
                      <Box key={pr.id} sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {pr.eventType}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatTime(pr.time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pr.location && `${pr.location} • `}
                          {formatDate(pr.date)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default SeasonHistorySection;

